import {
  EmploymentRecord,
  GroupedEmploymentData,
  OrganizationEmploymentGroup
} from '../types/employment.types';
import {
  createEmploymentPeriod,
  hasEmploymentGap,
  parseEmploymentDate
} from '../formatters/dateFormatters';

/**
 * Sort employment records by start date (most recent first)
 */
const sortByStartDateDesc = (records: EmploymentRecord[]): EmploymentRecord[] => {
  return [...records].sort((a, b) => {
    const dateA = parseEmploymentDate(a.startDate);
    const dateB = parseEmploymentDate(b.startDate);
    return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
  });
};

/**
 * Sort employment records by start date (oldest first)
 * Used for chronological grouping analysis
 */
const sortByStartDateAsc = (records: EmploymentRecord[]): EmploymentRecord[] => {
  return [...records].sort((a, b) => {
    const dateA = parseEmploymentDate(a.startDate);
    const dateB = parseEmploymentDate(b.startDate);
    return dateA.getTime() - dateB.getTime(); // Ascending order (oldest first)
  });
};

/**
 * Determine if two employment records should be in the same organization group
 * Based on chronological continuity (no gaps or employment at other organizations)
 */
const shouldGroupTogether = (
  record1: EmploymentRecord,
  record2: EmploymentRecord,
  allRecords: EmploymentRecord[]
): boolean => {
  // Must be same organization
  if (record1.organization.id !== record2.organization.id) {
    return false;
  }

  // Early validation
  if (!allRecords || allRecords.length === 0) {
    return false;
  }

  try {
    // Sort records chronologically to find the sequence
    const sortedRecords = sortByStartDateAsc(allRecords);
    
    const index1 = sortedRecords.findIndex(r => r.id === record1.id);
    const index2 = sortedRecords.findIndex(r => r.id === record2.id);
    
    if (index1 === -1 || index2 === -1) return false;
    
    const earlierIndex = Math.min(index1, index2);
    const laterIndex = Math.max(index1, index2);
    
    // Check if there are any records at different organizations between these two
    for (let i = earlierIndex + 1; i < laterIndex; i++) {
      const betweenRecord = sortedRecords[i];
      if (betweenRecord.organization.id !== record1.organization.id) {
        return false; // Different organization in between, should separate
      }
    }
    
    // Check for significant time gaps between consecutive periods at this organization
    const earlierRecord = sortedRecords[earlierIndex];
    const laterRecord = sortedRecords[laterIndex];
    
    // Find the end date of the earlier period and start date of the later period
    if (hasEmploymentGap(earlierRecord.endDate, laterRecord.startDate)) {
      // Check if there are roles at this organization that fill the gap
      const gapStart = parseEmploymentDate(earlierRecord.endDate === 'Present' ? earlierRecord.startDate : earlierRecord.endDate);
      const gapEnd = parseEmploymentDate(laterRecord.startDate);
      
      let hasFillingRole = false;
      for (let i = earlierIndex + 1; i < laterIndex; i++) {
        const fillerRecord = sortedRecords[i];
        if (fillerRecord.organization.id === record1.organization.id) {
          const fillerStart = parseEmploymentDate(fillerRecord.startDate);
          const fillerEnd = parseEmploymentDate(fillerRecord.endDate === 'Present' ? 'Present' : fillerRecord.endDate);
          
          // Check if this role overlaps with the gap period
          if (fillerStart <= gapEnd && fillerEnd >= gapStart) {
            hasFillingRole = true;
            break;
          }
        }
      }
      
      if (!hasFillingRole) {
        return false; // Gap exists and no role fills it
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in shouldGroupTogether:', error);
    return false;
  }
};

/**
 * Group employment records by organization with chronological separation
 * Main grouping algorithm following the business rules
 */
const groupEmploymentRecords = (records: EmploymentRecord[]): OrganizationEmploymentGroup[] => {
  if (!records || records.length === 0) {
    return [];
  }

  try {
    // Sort records chronologically for processing
    const sortedRecords = sortByStartDateAsc(records);
    const groups: OrganizationEmploymentGroup[] = [];
    const processedRecords = new Set<string>();

    for (const record of sortedRecords) {
      if (processedRecords.has(record.id)) {
        continue;
      }

      // Find all records that should be grouped with this one
      const groupRecords: EmploymentRecord[] = [record];
      processedRecords.add(record.id);

      // Look for other records at the same organization that should be grouped
      for (const otherRecord of sortedRecords) {
        if (processedRecords.has(otherRecord.id)) {
          continue;
        }

        if (shouldGroupTogether(record, otherRecord, sortedRecords)) {
          groupRecords.push(otherRecord);
          processedRecords.add(otherRecord.id);
        }
      }

      // Sort group records by recency (most recent first) for display
      const sortedGroupRecords = sortByStartDateDesc(groupRecords);

      // Calculate the overall period for this group
      // First check if any employment record has missing start date
      const hasAnyMissingStartDate = groupRecords.some(r => !r.startDate || r.startDate.trim() === '');
      
      let period;
      if (hasAnyMissingStartDate) {
        // If any record has missing start date, create an empty period (will be hidden in display)
        period = {
          start: '',
          end: '',
          duration: '',
        };
      } else {
        // All records have valid start dates, calculate period normally
        const allStartDates = groupRecords.map(r => parseEmploymentDate(r.startDate));
        const allEndDates = groupRecords.map(r => 
          r.endDate === 'Present' ? new Date() : parseEmploymentDate(r.endDate)
        );

        // Validate dates
        if (allStartDates.some(date => isNaN(date.getTime())) || 
            allEndDates.some(date => isNaN(date.getTime()))) {
          console.warn('Invalid dates found in employment records, skipping group');
          continue;
        }

        const earliestStart = new Date(Math.min(...allStartDates.map(d => d.getTime())));
        const latestEnd = new Date(Math.max(...allEndDates.map(d => d.getTime())));

        // Determine if any role is ongoing
        const hasOngoingRole = groupRecords.some(r => r.endDate === 'Present');
        const periodEndDate = hasOngoingRole ? 'Present' : latestEnd.toISOString().split('T')[0];

        period = createEmploymentPeriod(
          earliestStart.toISOString().split('T')[0],
          periodEndDate
        );
      }

      // Create organization group
      const organizationGroup: OrganizationEmploymentGroup = {
        organization: record.organization,
        period,
        employmentRecords: sortedGroupRecords,
        totalDuration: period.duration,
      };

      groups.push(organizationGroup);
    }

    return groups;
  } catch (error) {
    console.error('Error grouping employment records:', error);
    return [];
  }
};

/**
 * Sort organization groups by most recent employment period
 * Groups with ongoing positions appear first, then by most recent end date
 */
const sortOrganizationGroups = (groups: OrganizationEmploymentGroup[]): OrganizationEmploymentGroup[] => {
  try {
    return [...groups].sort((a, b) => {
      // Ongoing positions (Present) should come first
      if (a.period.end === 'Present' && b.period.end !== 'Present') {
        return -1;
      }
      if (b.period.end === 'Present' && a.period.end !== 'Present') {
        return 1;
      }

      // If both are ongoing or both are past, sort by start date of most recent role
      const aLatestStart = Math.max(...a.employmentRecords.map(r => {
        const date = parseEmploymentDate(r.startDate);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      }));
      const bLatestStart = Math.max(...b.employmentRecords.map(r => {
        const date = parseEmploymentDate(r.startDate);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      }));

      return bLatestStart - aLatestStart; // Most recent first
    });
  } catch (error) {
    console.error('Error sorting organization groups:', error);
    return groups;
  }
};

/**
 * Main function to transform flat employment list into grouped timeline data
 * This is the primary export used by the useEmploymentData hook
 */
export const transformToGroupedTimeline = (records: EmploymentRecord[]): GroupedEmploymentData => {
  if (!records || records.length === 0) {
    return {
      groups: [],
      totalRecords: 0,
      totalOrganizations: 0,
      isEmpty: true,
    };
  }

  try {
    // Group records by organization with chronological separation
    const groups = groupEmploymentRecords(records);
    
    // Sort groups by most recent employment period
    const sortedGroups = sortOrganizationGroups(groups);

    // Calculate statistics
    const uniqueOrganizations = new Set(records.map(r => r.organization.id));

    return {
      groups: sortedGroups,
      totalRecords: records.length,
      totalOrganizations: uniqueOrganizations.size,
      isEmpty: false,
    };
  } catch (error) {
    console.error('Error transforming to grouped timeline:', error);
    return {
      groups: [],
      totalRecords: 0,
      totalOrganizations: 0,
      isEmpty: true,
    };
  }
};

/**
 * Helper function to get organization logo URL
 * TODO: Implement when organization logo API is available
 */
export const getOrganizationLogo = (organizationId: number): string | undefined => {
  // Placeholder for future logo/avatar integration
  // This would integrate with the organization API to fetch logo URLs
  return undefined;
};

/**
 * Helper function to validate grouped employment data
 * Used for debugging and testing
 */
export const validateGroupedData = (data: GroupedEmploymentData): boolean => {
  try {
    if (data.isEmpty && data.groups.length > 0) {
      return false;
    }

    if (data.totalRecords !== data.groups.reduce((sum, group) => sum + group.employmentRecords.length, 0)) {
      return false;
    }

    // Validate each group has consistent organization data
    for (const group of data.groups) {
      if (group.employmentRecords.length === 0) {
        return false;
      }

      const orgId = group.organization.id;
      for (const record of group.employmentRecords) {
        if (record.organization.id !== orgId) {
          return false;
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating grouped data:', error);
    return false;
  }
}; 